import Box from '@mui/material/Box';
import type {
  ActiveElement, ChartEvent, ChartTypeRegistry, Point,
TooltipItem, } from 'chart.js';
import {
  Chart as ChartJS, registerables,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import type { OptionType } from 'common/components/Elements/Form/InputAdvancedSelection/common';
import { useAppSelector } from 'core/redux/store/configureStore';
import type { ScatterPlot,VariableMetaData as AxisVariable } from 'dataAnalyzer/api/dataAnalyzerApiClient';
import ChartAxisLabels from 'dataAnalyzer/components/ChartAxisLabels/ChartAxisLabels';
import ChartScrollableAxis from 'dataAnalyzer/components/ChartScrollableAxis/ChartScrollableAxis';
import type { BinnedPointToPosition } from 'dataAnalyzer/components/ChartView/ChartView';
import SimilarityTree from 'dataAnalyzer/components/SimilarityTree/SimilarityTree';
import type { GetTooltipLabel,
  GroupsColors, ScalesBoundaries} from 'dataAnalyzer/utilities/chart/chart';
import {
  buildConfig, getAnnotations, normalizePoints
} from 'dataAnalyzer/utilities/chart/chart';
import chartJSSelectionPlugin from 'dataAnalyzer/utilities/chartJSSelectionPlugin/chartJSSelectionPlugin';
import { getLabel } from 'dataAnalyzer/utilities/chartTooltipLabel/chartTooltipLabel';
import type {
AxisVariables,   ChartType, ChartView,   HandleScalesBoundariesChanged, JitterToggle,
  PointSize,
PointStyle,   ScrollableToggle, SelectionType,
SimilarityTreeToggle,
TooltipToggle, VisibleGroups, } from 'dataAnalyzer/utilities/dataAnalyzer.types';
import getFormattedDatasets from 'dataAnalyzer/utilities/getFormattedDatasets/getFormattedDatasets';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import isEqual from 'react-fast-compare';

import useHandleFinish from './useHandleFinish';
import useHandleSelect from './useHandleSelect';
import useScrollableChart from './useScrollableChart';

export const LEGEND_WIDTH = 22;

type ScatterChartJS = ChartJS<keyof ChartTypeRegistry, (number | Point | [number, number] | null)[], unknown>;

// Optimize with https://github.com/sgratzl/chartjs-chart-wordcloud/issues/4#issuecomment-1179123685
ChartJS.register(...registerables, zoomPlugin, annotationPlugin, chartJSSelectionPlugin);

function hasScalesBoundariesChanged(scalesBoundaries: ScalesBoundaries | undefined, chart: ScatterChartJS) {
  const x = chart.options.scales?.x;
  const y = chart.options.scales?.y;

  if (x && y && scalesBoundaries) {
    return x.min !== scalesBoundaries.minX
      || x.max !== scalesBoundaries.maxX
      || y.min !== scalesBoundaries.minY
      || y.max !== scalesBoundaries.maxY;
  }

  return false;
}

export type HandleClick = (
  event: ChartEvent,
  elements: ActiveElement[],
  chart: ScatterChartJS
) => void;

export interface ComponentProps {
  id: string;
  plotData: ScatterPlot;
  plotDataId: string;
  axisVariables: AxisVariables;
  visibleGroups: VisibleGroups;
  selectedIds: string[];
  onSelectedIdsChanged: (ids: string[]) => void;
  onScalesBoundariesChanged: HandleScalesBoundariesChanged;
  groupsColors: GroupsColors;
  chartType: ChartType;
  tooltipToggle: TooltipToggle;
  jitterToggle: JitterToggle;
  chartScalesBoundaries?: ScalesBoundaries;
  pointStyle: PointStyle;
  pointSize: PointSize;
  panningEnabled: boolean;
  selectionType: SelectionType;
  zoomEnabled: boolean;
  chartHeight: number;
  chartWidth: number;
  scrollable: ScrollableToggle;
  chartView: ChartView;
  xSimilarityTreeToggle: SimilarityTreeToggle;
  similarityTreeHeight: number;
  ySimilarityTreeToggle: SimilarityTreeToggle;
  similarityTreeWidth: number;
}

function Chart({
  id, plotData, plotDataId, axisVariables, visibleGroups, onSelectedIdsChanged,
  groupsColors, chartType, tooltipToggle, selectedIds,
  onScalesBoundariesChanged, jitterToggle, chartScalesBoundaries, pointStyle, pointSize, panningEnabled,
  selectionType, zoomEnabled, chartHeight: availableChartHeight, chartWidth: availableChartWidth,
  scrollable: scrollableToggle, chartView,
  xSimilarityTreeToggle, similarityTreeHeight,
  ySimilarityTreeToggle, similarityTreeWidth,
}: ComponentProps) {
  const configuration = useAppSelector((state) => state.configuration.configuration);

  const {
    xVariable: xVariableOption, yVariable: yVariableOption,
    xVariableScale, yVariableScale,
    xVariableDirection, yVariableDirection,
    xVariableDataType, yVariableDataType,
  } = axisVariables;

  const xVariable = (xVariableOption as OptionType<AxisVariable>).data as AxisVariable;
  const yVariable = (yVariableOption as OptionType<AxisVariable>).data as AxisVariable;

  const scrollable = scrollableToggle === 'on';

  const [xBinnedPointToPosition, setXBinnedPointToPosition] = useState<BinnedPointToPosition>({});
  const [yBinnedPointToPosition, setYBinnedPointToPosition] = useState<BinnedPointToPosition>({});

  /**
   * The chart ID defines whether a chart should be rebuild or updated.
   * Any change in the following variables should trigger the chart to be discarded and rebuild.
   *
   * Examples:
   * Rebuild: happens when an axis variable or data type is changed (new API data)
   * Update: happens when the user zoom (new API data)
   * Rebuild: happens when the axis variable direction is changed (no API call)
   * Update: happens when the user change visible groups (no API call)
   */
  const getChartId = useCallback(() => ([
    plotDataId,
    xVariableScale,
    yVariableScale,
    xVariableDirection,
    yVariableDirection,
    chartType,
    tooltipToggle,
    pointStyle,
    pointSize,
    scrollable,
  ]), [
    plotDataId,
    xVariableScale,
    yVariableScale,
    xVariableDirection,
    yVariableDirection,
    chartType,
    tooltipToggle,
    pointStyle,
    pointSize,
    scrollable,
  ]);

  const myChart = useRef<ScatterChartJS | undefined>();

  const {
    scrollableRef, chartXAxisRef, chartYAxisRef,
    chartHeight, chartWidth,
    availableChartWidthWithOptionalScrollbar, availableChartHeighthWithOptionalScrollbar,
  } = useScrollableChart(plotData, scrollable, availableChartHeight, availableChartWidth);

  /** chartId is a flag that will trigger re-draw of the chart based on some variables changes */
  const [chartId, setChartId] = useState(getChartId());

  /**
   * Handle selection
   */
  const { handleSelect, handleClickCallback, handleOnSelectedBinsChanged } = useHandleSelect(
    plotData.points,
    selectedIds,
    onSelectedIdsChanged,
    chartView,
    chartType,
    xVariableDataType,
    yVariableDataType,
    visibleGroups,
    selectionType,
    plotData.xUniqueValues || [],
    plotData.yUniqueValues || [],
  );

  const handleBinnedPointToPositionChanged = useCallback((points: BinnedPointToPosition, axis: 'x' | 'y') => {
    if (plotData?.xDistanceTree || plotData?.yDistanceTree) {
      if (axis === 'x') setXBinnedPointToPosition(points);
      if (axis === 'y') setYBinnedPointToPosition(points);
    }
  }, [plotData?.xDistanceTree, plotData?.yDistanceTree]);

  /**
   * Copy axis out of the chart canvas when the chart is scrollable chart
   */
  const { handleFinish, chartAxisSizes } = useHandleFinish(
    id, scrollable, plotData, handleBinnedPointToPositionChanged,
  );

  const getTooltipLabel: GetTooltipLabel = useCallback((
    tooltipItem: TooltipItem<'scatter' | 'bar'>,
  ) => getLabel(tooltipItem, visibleGroups, chartView, axisVariables, configuration),
  [chartView, visibleGroups, axisVariables, configuration]);

  useEffect(() => {
    let preservedScalesBoundaries: ScalesBoundaries | undefined;

    if (!isEqual(chartId, getChartId()) && myChart.current) {
      const x = myChart.current.options.scales?.x;
      const y = myChart.current.options.scales?.y;

      // For some variables changes (chartType, tooltipToggle, pointStyle, pointSize) we want to preserve the current zoom level
      if (
        (
          chartId[5] !== getChartId()[5]
          || chartId[6] !== getChartId()[6]
          || chartId[7] !== getChartId()[7]
          || chartId[8] !== getChartId()[8]
        )
        && x && y
      ) {
        preservedScalesBoundaries = {
          minX: x.min as number,
          maxX: x.max as number,
          minY: y.min as number,
          maxY: y.max as number,
        };
      }

      myChart.current.destroy();
      myChart.current = undefined;
    }

    if (!myChart.current) {
      const element = document.getElementById(id) as HTMLCanvasElement;

      const config = buildConfig(
        plotData,
        visibleGroups,
        selectedIds,
        xVariable,
        yVariable,
        handleSelect,
        handleClickCallback,
        onScalesBoundariesChanged,
        xVariableScale,
        yVariableScale,
        xVariableDirection,
        yVariableDirection,
        xVariableDataType,
        yVariableDataType,
        groupsColors,
        chartType,
        tooltipToggle,
        jitterToggle,
        preservedScalesBoundaries,
        getTooltipLabel,
        pointStyle,
        pointSize,
        handleFinish,
        scrollable,
        chartView,
      );

      myChart.current = new ChartJS(element, config);
    } else {
      const newFormattedDatasets = getFormattedDatasets(
        plotData.points,
        visibleGroups,
        selectedIds,
        xVariable,
        yVariable,
        xVariableDataType,
        yVariableDataType,
        plotData.groupVariable,
        groupsColors,
        chartType,
        jitterToggle,
        chartView,
        plotData.xUniqueValues || [],
        plotData.yUniqueValues || [],
        plotData.groupUniqueValues || [],
        pointSize,
      );

      // Need to re-bind selection callbacks otherwise it will lose context
      if (myChart.current.options.plugins?.selection) {
        if (panningEnabled || zoomEnabled) {
          myChart.current.options.plugins.selection.onSelect = undefined;
        } else {
          myChart.current.options.plugins.selection.onSelect = handleSelect;
        }
      }

      myChart.current.options.onClick = handleClickCallback;

      // Turn on/off zoom when zoomEnabled
      const prevZoomEnabled = myChart.current.options.plugins?.zoom?.zoom?.drag?.enabled;

      if (!scrollable && myChart.current.options.plugins?.zoom?.zoom?.drag) {
        myChart.current.options.plugins.zoom.zoom.drag.enabled = zoomEnabled;
      }

      // Turn on/off panning when panningEnabled
      const prevPanningEnabled = myChart.current.options.plugins?.zoom?.pan?.enabled;

      if (!scrollable && myChart.current.options.plugins?.zoom?.pan) {
        myChart.current.options.plugins.zoom.pan.enabled = panningEnabled;
      }

      if (
        isEqual(myChart.current.data.datasets, newFormattedDatasets)
        && !hasScalesBoundariesChanged(chartScalesBoundaries, myChart.current)
        && prevPanningEnabled === panningEnabled
        && prevZoomEnabled === zoomEnabled
      ) {
        return;
      }

      myChart.current.data.datasets = newFormattedDatasets;

      // Update annotations as their position might change on the bar chart based on group visibility
      if (chartType === 'bars') {
        const {
          pointsXValuesIndexes, pointsYValuesIndexes,
        } = normalizePoints(
          plotData.points,
          chartView,
          xVariableDataType,
          yVariableDataType,
          plotData.xUniqueValues || [],
          plotData.yUniqueValues || [],
        );

        const newAnnotations = getAnnotations(
          newFormattedDatasets,
          chartType,
          pointsXValuesIndexes,
          pointsYValuesIndexes,
          plotData.xBoxPlot,
          plotData.yBoxPlot,
        );

        if (myChart.current.options.plugins?.annotation) {
          myChart.current.options.plugins.annotation = { annotations: newAnnotations };
        }
      }

      if (myChart.current.options.plugins?.tooltip?.callbacks) {
        myChart.current.options.plugins.tooltip.callbacks.title = () => '';
        myChart.current.options.plugins.tooltip.callbacks.label = getTooltipLabel;
      }

      // Keep the current scales boundaries otherwise the view might shift
      const x = myChart.current.options.scales?.x;
      if (x && chartScalesBoundaries) {
        x.min = chartScalesBoundaries.minX;
        x.max = chartScalesBoundaries.maxX;
      }

      const y = myChart.current.options.scales?.y;
      if (y && chartScalesBoundaries) {
        y.min = chartScalesBoundaries.minY;
        y.max = chartScalesBoundaries.maxY;
      }

      myChart.current.update();

      // Avoid updating scales boundaries when we have just reset the view to its initial boundaries
      if (chartScalesBoundaries && !chartScalesBoundaries.initial && chartView !== 'histogram') {
        onScalesBoundariesChanged(undefined);
      }
    }
  }, [
    chartId,
    getChartId,
    handleSelect,
    handleClickCallback,
    onScalesBoundariesChanged,
    id,
    plotData,
    selectedIds,
    visibleGroups,
    xVariable,
    yVariable,
    xVariableScale,
    yVariableScale,
    xVariableDirection,
    yVariableDirection,
    xVariableDataType,
    yVariableDataType,
    groupsColors,
    chartType,
    tooltipToggle,
    jitterToggle,
    chartScalesBoundaries,
    getTooltipLabel,
    pointStyle,
    pointSize,
    panningEnabled,
    zoomEnabled,
    handleFinish,
    scrollable,
    chartView,
  ]);

  useEffect(() => {
    setChartId(getChartId());
  }, [getChartId]);

  let cursor: undefined | 'move' | 'zoom-in';
  if (panningEnabled) cursor = 'move';
  if (zoomEnabled) cursor = 'zoom-in';

  return (
    <Box
      ref={scrollableRef}
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: scrollable ? 'scroll' : 'hidden',
      }}
    >
      <ChartAxisLabels
        yVariable={yVariable}
        xVariable={xVariable}
        chartWidth={availableChartWidthWithOptionalScrollbar}
        chartHeight={availableChartHeighthWithOptionalScrollbar}
      />
      {scrollable && chartAxisSizes && (
        <ChartScrollableAxis
          chartAxisSizes={chartAxisSizes}
          chartYAxisRef={chartYAxisRef}
          chartXAxisRef={chartXAxisRef}
          availableChartHeighthWithOptionalScrollbar={availableChartHeighthWithOptionalScrollbar}
          availableChartWidthWithOptionalScrollbar={availableChartWidthWithOptionalScrollbar}
          availableChartHeight={availableChartHeight}
          availableChartWidth={availableChartWidth}
        />
      )}
      {
        ySimilarityTreeToggle.match('on')
        && plotData?.yDistanceTree
        && Object.keys(yBinnedPointToPosition).length > 0 && (
          <Box sx={{
            position: 'absolute',
            left: `${LEGEND_WIDTH}px`,
            width: `${similarityTreeWidth}px`,
            zIndex: 1,
          }}
          >
            <SimilarityTree
              orientation="vertical"
              height={chartHeight}
              width={similarityTreeWidth}
              binnedPointToPosition={yBinnedPointToPosition}
              distanceTree={plotData?.yDistanceTree}
              toggle={ySimilarityTreeToggle}
              legendPosition={chartAxisSizes.yAxisHeight}
              onOnSelectedBinsChanged={(indexes) => handleOnSelectedBinsChanged('y', indexes, plotData)}
            />
          </Box>
        )
      }
      {
        xSimilarityTreeToggle.match('on')
        && plotData?.xDistanceTree
        && Object.keys(xBinnedPointToPosition).length > 0 && (
          <Box sx={{
            position: 'absolute',
            bottom: `${LEGEND_WIDTH}px`,
            left: `${LEGEND_WIDTH + similarityTreeWidth}px`,
            height: `${similarityTreeHeight}px`,
            zIndex: 1,
          }}
          >
            <SimilarityTree
              orientation="horizontal"
              height={similarityTreeHeight}
              width={chartWidth - LEGEND_WIDTH}
              binnedPointToPosition={xBinnedPointToPosition}
              distanceTree={plotData.xDistanceTree}
              toggle={xSimilarityTreeToggle}
              legendPosition={chartAxisSizes.xAxisLeft}
              onOnSelectedBinsChanged={(indexes) => handleOnSelectedBinsChanged('x', indexes, plotData)}
            />
          </Box>
        )
      }
      <Box
        sx={{
          position: 'relative',
          height: scrollable ? `${chartHeight}px` : `calc(100% - ${similarityTreeHeight}px - ${LEGEND_WIDTH}px)`,
          width: scrollable ? `${chartWidth}px` : `calc(100% - ${similarityTreeWidth}px - ${LEGEND_WIDTH}px)`,
          margin: `0 0 ${LEGEND_WIDTH}px ${LEGEND_WIDTH + similarityTreeWidth}px`,
          cursor,
        }}
      >
        <canvas id={id} />
      </Box>
    </Box>
  );
}
export default Chart;
